// Borrowing Capacity Calculator
// Uses standard Canadian mortgage payment formula to calculate maximum loan amount

export interface BorrowingCapacityResult {
  mortgageAmount: number;
  totalPayments: number;
  totalInterest: number;
}

/**
 * Calculate maximum mortgage amount based on payment capacity
 * Formula: P = M × [(1 - (1 + r)^-n) / r]
 *
 * @param paymentAmount - Monthly/bi-weekly/weekly payment amount
 * @param annualRate - Annual interest rate (as percentage, e.g., 5.25)
 * @param amortizationYears - Amortization period in years
 * @param frequency - Payment frequency ('monthly' | 'biweekly' | 'weekly')
 * @returns Maximum mortgage amount that can be borrowed
 */
export const calculateBorrowingCapacity = (
  paymentAmount: number,
  annualRate: number,
  amortizationYears: number,
  frequency: 'monthly' | 'biweekly' | 'weekly'
): BorrowingCapacityResult => {
  // Convert annual rate to decimal
  const annualRateDecimal = annualRate / 100;

  // Determine payment frequency multiplier
  let paymentsPerYear: number;
  switch (frequency) {
    case 'biweekly':
      paymentsPerYear = 26;
      break;
    case 'weekly':
      paymentsPerYear = 52;
      break;
    default:
      paymentsPerYear = 12;
  }

  // Calculate rate per payment period
  const ratePerPeriod = annualRateDecimal / paymentsPerYear;

  // Calculate total number of payments
  const totalPayments = amortizationYears * paymentsPerYear;

  // Calculate mortgage amount using present value formula
  // P = M × [(1 - (1 + r)^-n) / r]
  const mortgageAmount = paymentAmount * ((1 - Math.pow(1 + ratePerPeriod, -totalPayments)) / ratePerPeriod);

  // Calculate total amount paid and interest
  const totalPaid = paymentAmount * totalPayments;
  const totalInterest = totalPaid - mortgageAmount;

  return {
    mortgageAmount: Math.round(mortgageAmount),
    totalPayments: totalPayments,
    totalInterest: Math.round(totalInterest),
  };
};

/**
 * Generate AI insights based on borrowing capacity
 */
export const getBorrowingInsights = (
  mortgageAmount: number,
  paymentAmount: number,
  interestRate: number
): string[] => {
  const insights: string[] = [];

  // Home price estimation (assuming 20% down payment)
  const estimatedHomePrice = mortgageAmount / 0.8;
  insights.push(
    `💰 **Estimated Home Budget:** With this borrowing capacity, you can explore homes priced around $${estimatedHomePrice.toLocaleString()} (assuming 20% down payment).`
  );

  // Payment to income ratio check
  const assumedMonthlyIncome = (paymentAmount / 0.32) * (12 / 12); // Assuming 32% of income
  insights.push(
    `📊 **Income Consideration:** Based on the 32% gross debt service ratio guideline, this suggests a monthly income of approximately $${assumedMonthlyIncome.toLocaleString()}.`
  );

  // Interest rate insight
  if (interestRate > 5.0) {
    insights.push(
      `🔍 **Rate Shopping Tip:** With rates at ${interestRate.toFixed(2)}%, shopping for a rate just 0.25% lower could increase your borrowing power by $${(mortgageAmount * 0.03).toLocaleString()}.`
    );
  }

  // Amortization insight
  insights.push(
    '⚡ **Increase Your Power:** Extending your amortization from 25 to 30 years could increase your borrowing capacity by approximately 10%, though you\'ll pay more interest over time.'
  );

  return insights;
};
